/**
 * Global Stats Component
 * Displays collaborative statistics
 */
import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Chip, Tooltip } from '@mui/material';
import { TrendingUp, People, Article, Schedule } from '@mui/icons-material';
import { useUpload } from '@/contexts/UploadContext';
import { useTranslation } from 'react-i18next';

const GlobalStats: React.FC = () => {
  const { t } = useTranslation();
  const { stats } = useUpload();
  
  if (!stats) return null;
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="text.secondary" variant="body2">
                  {t('upload.stats.in_progress', 'In Progress')}
                </Typography>
                <Typography variant="h4">{stats.batches_in_progress}</Typography>
              </Box>
              <Schedule sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="text.secondary" variant="body2">
                  {t('upload.stats.today', 'Today')}
                </Typography>
                <Typography variant="h4">{stats.batches_today}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {stats.total_files_processed_today} {t('upload.stats.files', 'files')}
                </Typography>
              </Box>
              <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="text.secondary" variant="body2">
                  {t('upload.stats.this_month', 'This Month')}
                </Typography>
                <Typography variant="h4">{stats.batches_this_month}</Typography>
              </Box>
              <People sx={{ fontSize: 40, color: 'info.main' }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="text.secondary" variant="body2">
                  {t('upload.stats.articles_today', 'Retombées Aujourd\'hui')}
                </Typography>
                <Typography variant="h4">{stats.total_articles_extracted_today}</Typography>
              </Box>
              <Article sx={{ fontSize: 40, color: 'warning.main' }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      {stats.recent_clients.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('upload.stats.recent_clients', 'Recent Clients')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {stats.recent_clients.map((client) => {
                  const truncatedLabel = client.length > 60 ? client.substring(0, 60) + '...' : client;
                  return (
                    <Tooltip key={client} title={client} arrow>
                      <Chip label={truncatedLabel} variant="outlined" sx={{ maxWidth: 300 }} />
                    </Tooltip>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default GlobalStats;

